/**
 * GA4 Analytics Service
 *
 * Dynamic GA4 script injection with Consent Mode v2 defaults.
 * No-ops gracefully when VITE_GA4_MEASUREMENT_ID is not set (dev mode).
 */

// Extend window for gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type GtagCommand = 'config' | 'event' | 'consent' | 'set' | 'js';

interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

interface TranslationEventParams {
  source_language: string;
  target_language: string;
  translation_mode: string;
  latency_ms: number;
}

let initialized = false;
let measurementId: string | null = null;

/**
 * Returns true if analytics is configured and available.
 */
function isEnabled(): boolean {
  return initialized && measurementId !== null;
}

/**
 * Type-safe gtag wrapper. No-ops if analytics is not initialized.
 */
function gtag(command: GtagCommand, ...args: unknown[]): void {
  if (!isEnabled() && command !== 'consent') return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(command, ...args);
  }
}

/**
 * Set Consent Mode v2 defaults — deny all analytics/ad cookies until
 * the user explicitly grants consent via the CookieConsent component.
 */
function setConsentDefaults(): void {
  // Initialize dataLayer before gtag.js loads
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };

  // Consent Mode v2: default to denied for all cookie categories
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted', // security cookies are always OK
    wait_for_update: 500, // ms to wait for consent update before firing tags
  });
}

/**
 * Dynamically inject the gtag.js script into the page.
 * Only injects once, even if called multiple times.
 */
function injectGtagScript(id: string): void {
  if (document.getElementById('ga4-gtag')) return;

  const script = document.createElement('script');
  script.id = 'ga4-gtag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  // Initialize gtag with timestamp and config
  window.gtag('js', new Date());
  window.gtag('config', id, {
    send_page_view: false, // we'll send page views manually for SPA routing
  });
}

/**
 * Initialize GA4 analytics.
 * Call once on app mount. No-ops if measurement ID is not provided.
 *
 * @param id - GA4 measurement ID (G-XXXXXXXXXX). Defaults to VITE_GA4_MEASUREMENT_ID env var.
 */
export function initAnalytics(id?: string): void {
  if (initialized) return;

  const resolvedId = id || import.meta.env.VITE_GA4_MEASUREMENT_ID;

  if (!resolvedId) {
    if (import.meta.env.DEV) {
      console.debug('[Analytics] No GA4 measurement ID configured — analytics disabled');
    }
    return;
  }

  measurementId = resolvedId;

  // Set consent defaults BEFORE loading the script
  setConsentDefaults();

  // Inject the gtag.js script
  injectGtagScript(resolvedId);

  initialized = true;

  if (import.meta.env.DEV) {
    console.debug(`[Analytics] Initialized with measurement ID: ${measurementId}`);
  }
}

/**
 * Track a page view. Call on route changes in SPA.
 */
export function trackPageView(path: string, title?: string): void {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

/**
 * Track a custom event.
 */
export function trackEvent(name: string, params?: EventParams): void {
  gtag('event', name, params);
}

/**
 * Track a translation event with structured params.
 */
export function trackTranslation(
  sourceLang: string,
  targetLang: string,
  mode: string,
  latencyMs: number
): void {
  const params: TranslationEventParams = {
    source_language: sourceLang,
    target_language: targetLang,
    translation_mode: mode,
    latency_ms: latencyMs,
  };
  gtag('event', 'translation_complete', params);
}

/**
 * Link GA4 client ID and UTM params to an authenticated Supabase user.
 * Fire-and-forget: does not block auth flow, silently swallows errors.
 * Call once after successful sign-in.
 */
export async function linkAnalyticsToUser(
  userId: string,
  supabaseClient: { from: (table: string) => { insert: (row: Record<string, unknown>) => { then: (fn: (res: unknown) => void) => void } } }
): Promise<void> {
  try {
    // Dynamic import to avoid circular deps
    const { getStoredUtmParams, getGA4ClientId } = await import('./utmCapture');

    const utmParams = getStoredUtmParams();
    const ga4ClientId = getGA4ClientId();

    // Nothing to link — skip the write
    if (!utmParams && !ga4ClientId) return;

    const row: Record<string, unknown> = {
      user_id: userId,
      ga4_client_id: ga4ClientId || null,
      utm_source: utmParams?.utm_source || null,
      utm_medium: utmParams?.utm_medium || null,
      utm_campaign: utmParams?.utm_campaign || null,
      utm_content: utmParams?.utm_content || null,
      utm_term: utmParams?.utm_term || null,
      landing_url: window.location.href,
    };

    // Fire-and-forget insert — no await on the caller side needed
    supabaseClient.from('user_analytics').insert(row).then(() => {
      if (import.meta.env.DEV) {
        console.debug('[Analytics] Linked analytics to user:', userId);
      }
    });
  } catch {
    // Silent failure — analytics linking must never break auth
    if (import.meta.env.DEV) {
      console.debug('[Analytics] Failed to link analytics to user (non-blocking)');
    }
  }
}

/**
 * Update consent state. Called by the CookieConsent component when user grants consent.
 */
export function updateConsent(granted: boolean): void {
  const state = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    functionality_storage: state,
    personalization_storage: state,
  });
}
